import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {v4 as uuid} from 'uuid';
import differenceBy from 'lodash/differenceBy';

import {isSupplementAnchor} from './annotationAnchors';
import {combineNotes} from './annotationMerge';
import {resetPaintCoordinator} from './annotationPaintCoordinator';
import type {TextProjection} from './annotationTextProjection';
import type {
  Anchor,
  Annotation,
  AnnotationMerge,
  AnnotationType,
  HighlightColor
} from './annotationTypes';

/** An annotatable supplement on the current question, one entry per mount. */
export interface MountedSupplement {
  regionId: string;
  name: string;
  type: string;
}

/** An annotatable region mounted with note indicators, one entry per mount. */
export interface IndicatorRegion {
  regionId: string;
  container: HTMLElement;
  getProjection: () => TextProjection | null;
}

export interface AnnotationModeState {
  isAnnotationModeOn: boolean;
  annotationsByRegionId: Record<string, Annotation[]>;
  defaultColor: HighlightColor;
  defaultType: AnnotationType;
  /** Mounted surfaces with annotatable content; only "any vs none" is read. */
  annotatableContentMounts: number;
  /** Whether the notes side panel is showing. Outlives the mode being turned off. */
  isNotesPanelOpen: boolean;
  /** In mount order, which is on-screen order; a split layout lists a mount twice. */
  mountedSupplements: MountedSupplement[];
  /** True once persisted marks have been merged in. Read by the sync layer only. */
  isHydrated: boolean;
  /** In mount order; read by AnnotationNoteIndicators to draw the margin buttons. */
  indicatorRegions: IndicatorRegion[];
}

export interface AnnotationModeActions {
  toggleAnnotationMode: () => void;
  addAnnotation: (anchor: Anchor) => string;
  mergeAnnotations: (merge: AnnotationMerge) => void;
  setHighlightColor: (regionId: string, id: string, color: HighlightColor) => void;
  setAnnotationUnderline: (regionId: string, id: string) => void;
  setAnnotationNote: (regionId: string, id: string, note: string) => void;
  removeAnnotation: (regionId: string, id: string) => void;
  /** Merges persisted marks in by id; local wins, hydrated marks go first in a bucket. */
  hydrate: (byRegionId: Record<string, Annotation[]>) => void;
  registerAnnotatableContent: () => void;
  unregisterAnnotatableContent: () => void;
  registerSupplement: (supplement: MountedSupplement) => void;
  unregisterSupplement: (regionId: string) => void;
  registerIndicatorRegion: (region: IndicatorRegion) => void;
  unregisterIndicatorRegion: (region: IndicatorRegion) => void;
  openNotesPanel: () => void;
  closeNotesPanel: () => void;
  reset: () => void;
}

const initialState: AnnotationModeState = {
  isAnnotationModeOn: false,
  annotationsByRegionId: {},
  defaultColor: 'yellow',
  defaultType: 'highlight',
  annotatableContentMounts: 0,
  isNotesPanelOpen: false,
  mountedSupplements: [],
  isHydrated: false,
  indicatorRegions: []
};

/**
 * A whole-supplement mark paints as a border, which an underline cannot express,
 * so it takes the first highlight color rather than the sticky underline default.
 */
function styleFor(
  anchor: Anchor,
  {defaultColor, defaultType}: AnnotationModeState
): {color: HighlightColor; type: AnnotationType} {
  if (isSupplementAnchor(anchor) && defaultType === 'underline') {
    return {color: 'yellow', type: 'highlight'};
  }
  return {color: defaultColor, type: defaultType};
}

/** Returns `local` itself when nothing new arrives. */
function mergeHydratedAnnotations(
  local: Record<string, Annotation[]>,
  hydrated: Record<string, Annotation[]>
): Record<string, Annotation[]> {
  const merged = {...local};
  let changed = false;
  for (const [regionId, incoming] of Object.entries(hydrated)) {
    const existing = local[regionId] ?? [];
    const fresh = differenceBy(incoming, existing, 'id');
    if (fresh.length > 0) {
      merged[regionId] = [...fresh, ...existing];
      changed = true;
    }
  }
  return changed ? merged : local;
}

export const useAnnotationModeStore = create<AnnotationModeState & AnnotationModeActions>()(
  devtools(
    (set) => ({
      ...initialState,

      toggleAnnotationMode: () => {
        set(
          (state) => ({isAnnotationModeOn: !state.isAnnotationModeOn}),
          false,
          'toggleAnnotationMode'
        );
      },

      // The anchor is authoritative: its regionId names the bucket.
      addAnnotation: (anchor: Anchor) => {
        const id = uuid();
        set(
          (state) => ({
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [anchor.regionId]: [
                ...(state.annotationsByRegionId[anchor.regionId] ?? []),
                {id, anchor, ...styleFor(anchor, state)}
              ]
            }
          }),
          false,
          'addAnnotation'
        );
        return id;
      },

      // Absorbed notes are read at apply time, not baked into the payload: a note
      // flushed between the merge gesture and its confirmation still counts.
      mergeAnnotations: ({intoId, absorbedIds, anchor}: AnnotationMerge) => {
        set(
          (state) => {
            const annotations = state.annotationsByRegionId[anchor.regionId] ?? [];
            const absorbedNotes = absorbedIds.map(
              (absorbedId) => annotations.find((annotation) => annotation.id === absorbedId)?.note
            );
            return {
              annotationsByRegionId: {
                ...state.annotationsByRegionId,
                [anchor.regionId]: annotations
                  .filter((annotation) => !absorbedIds.includes(annotation.id))
                  .map((annotation) =>
                    annotation.id === intoId
                      ? {...annotation, anchor, note: combineNotes(annotation.note, absorbedNotes)}
                      : annotation
                  )
              }
            };
          },
          false,
          'mergeAnnotations'
        );
      },

      // Recolor one annotation as a highlight and make that color/type the default for new ones.
      setHighlightColor: (regionId: string, id: string, color: HighlightColor) => {
        set(
          (state) => ({
            defaultColor: color,
            defaultType: 'highlight',
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).map((annotation) =>
                annotation.id === id ? {...annotation, color, type: 'highlight'} : annotation
              )
            }
          }),
          false,
          'setHighlightColor'
        );
      },

      // Switch one annotation to underline (mutually exclusive with color) and make it the default.
      setAnnotationUnderline: (regionId: string, id: string) => {
        set(
          (state) => ({
            defaultType: 'underline',
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).map((annotation) =>
                annotation.id === id ? {...annotation, type: 'underline'} : annotation
              )
            }
          }),
          false,
          'setAnnotationUnderline'
        );
      },

      // A blank note is a deleted note: the field goes absent, never ''.
      setAnnotationNote: (regionId: string, id: string, note: string) => {
        set(
          (state) => ({
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).map((annotation) =>
                annotation.id === id
                  ? {...annotation, note: note.trim() ? note : undefined}
                  : annotation
              )
            }
          }),
          false,
          'setAnnotationNote'
        );
      },

      removeAnnotation: (regionId: string, id: string) => {
        set(
          (state) => ({
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).filter(
                (annotation) => annotation.id !== id
              )
            }
          }),
          false,
          'removeAnnotation'
        );
      },

      hydrate: (byRegionId: Record<string, Annotation[]>) => {
        set(
          (state) => ({
            annotationsByRegionId: mergeHydratedAnnotations(
              state.annotationsByRegionId,
              byRegionId
            ),
            isHydrated: true
          }),
          false,
          'hydrate'
        );
      },

      registerSupplement: (supplement: MountedSupplement) => {
        set(
          (state) => ({mountedSupplements: [...state.mountedSupplements, supplement]}),
          false,
          'registerSupplement'
        );
      },

      unregisterSupplement: (regionId: string) => {
        set(
          (state) => {
            // One entry per mount, so an unmount removes exactly one.
            const index = state.mountedSupplements.findIndex(
              (mounted) => mounted.regionId === regionId
            );
            if (index === -1) {
              return {};
            }
            return {
              mountedSupplements: state.mountedSupplements.filter((_, i) => i !== index)
            };
          },
          false,
          'unregisterSupplement'
        );
      },

      registerIndicatorRegion: (region: IndicatorRegion) => {
        set(
          (state) => ({indicatorRegions: [...state.indicatorRegions, region]}),
          false,
          'registerIndicatorRegion'
        );
      },

      // By entry identity, so a dual-mounted region's other mount stays registered.
      unregisterIndicatorRegion: (region: IndicatorRegion) => {
        set(
          (state) => {
            if (!state.indicatorRegions.includes(region)) {
              return {};
            }
            return {
              indicatorRegions: state.indicatorRegions.filter((mounted) => mounted !== region)
            };
          },
          false,
          'unregisterIndicatorRegion'
        );
      },

      registerAnnotatableContent: () => {
        set(
          (state) => ({annotatableContentMounts: state.annotatableContentMounts + 1}),
          false,
          'registerAnnotatableContent'
        );
      },

      unregisterAnnotatableContent: () => {
        set(
          (state) => ({annotatableContentMounts: Math.max(0, state.annotatableContentMounts - 1)}),
          false,
          'unregisterAnnotatableContent'
        );
      },

      // Not a toggle: a second indicator click must not hide a panel being read.
      openNotesPanel: () => {
        set({isNotesPanelOpen: true}, false, 'openNotesPanel');
      },

      closeNotesPanel: () => {
        set({isNotesPanelOpen: false}, false, 'closeNotesPanel');
      },

      reset: () => {
        // Or stale highlights would outlive an emptied store.
        resetPaintCoordinator();
        // Regions are mount-owned and unregister themselves; a session reset runs
        // after their effects, so wiping them here strands every live mount.
        set(
          (state) => ({...initialState, indicatorRegions: state.indicatorRegions}),
          false,
          'reset'
        );
      }
    }),
    {name: 'AnnotationModeStore'}
  )
);

/**
 * Hook that resets the annotation mode store on mount and unmount.
 * Call this at the practice-session level (e.g. AssignmentPracticeView), not
 * inside the per-question subtree — PracticeView remounts on every question
 * navigation and the mode must stay on across questions.
 */
export function useAnnotationModeReset() {
  const reset = useAnnotationModeStore((state) => state.reset);

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, [reset]);
}

/**
 * Counts this surface toward the banner's "No content to highlight" truth
 * while it is mounted with content.
 */
export function useAnnotatableContentPresence(hasContent: boolean) {
  const register = useAnnotationModeStore((state) => state.registerAnnotatableContent);
  const unregister = useAnnotationModeStore((state) => state.unregisterAnnotatableContent);

  useEffect(() => {
    if (!hasContent) {
      return undefined;
    }
    register();
    return unregister;
  }, [hasContent, register, unregister]);
}

/** Lists this supplement on the current question for as long as it is mounted. */
export function useSupplementPresence(regionId: string, name: string, type: string) {
  const register = useAnnotationModeStore((state) => state.registerSupplement);
  const unregister = useAnnotationModeStore((state) => state.unregisterSupplement);

  useEffect(() => {
    register({regionId, name, type});
    return () => unregister(regionId);
  }, [regionId, name, type, register, unregister]);
}

/** Lists the region for indicator rendering while it is mounted; null regionId opts out. */
export function useIndicatorRegion(
  regionId: string | null,
  container: HTMLElement | null,
  getProjection: () => TextProjection | null
): void {
  const register = useAnnotationModeStore((state) => state.registerIndicatorRegion);
  const unregister = useAnnotationModeStore((state) => state.unregisterIndicatorRegion);

  useEffect(() => {
    if (!regionId || !container) {
      return undefined;
    }
    const region = {regionId, container, getProjection};
    register(region);
    return () => unregister(region);
  }, [regionId, container, getProjection, register, unregister]);
}
